import logo from "./logo.svg";
import styles from "./App.module.css";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useNavigate,
  useLocation,
} from "react-router-dom";
import React, { useEffect, useRef, useState } from "react";
import Browse from "./Containers/Browse/Browse";
import GamePage from "./Containers/GamePage/GamePage";
import GameDetail from "./Containers/GameDetail/GameDetail";
import CollectionDetail from "./Containers/CollectionDetail/CollectionDetail";
import TopicDetail from "./Containers/TopicDetail/TopicDetail";
import PostDetail from "./Containers/PostDetail/PostDetail";
import NotFound from "./Containers/NotFound/NotFound";
import Home from "./Containers/Home/Home";
import Profile from "./Containers/Profile/Profile";
import { AnimatePresence } from "framer-motion";
import filterNames from "./utils/filterNames";
import games from "./utils/games";
import templateGame from "./utils/templateGame";
import { ToastProvider } from "./Components/Toast/Toast";
import { ResponsiveProvider } from "./Components/Responsive";
import VKCallback from "./Components/Login/VKCallback";
import { get, post } from "./utils/request";
import BackTop from "./Components/BackTop/BackTop";
import { initI18n } from "./utils/i18n";
import {
  readStoredTheme,
  applyThemeToDocument,
  THEME_STORAGE_KEY,
} from "./utils/themeStorage";
import { SESSION_INVALID_EVENT } from "./utils/authSession";
import {
  fetchAndCacheCurrentUser,
  clearCurrentUserCache,
  getCachedCurrentUserId,
} from "./utils/currentUser";
import {
  isLangCategoryRouting,
  browsePath,
  gameHomePath,
  legacyGamePagePath,
  isGameHomePath,
} from "./utils/routes";
import {
  LangCategoryLayout,
  LangDetailLayout,
  LangOnlyRedirect,
} from "./Components/LangRouteGuard/LangRouteGuard";

function App() {
  const yandexMetricaId = Number(process.env.REACT_APP_YANDEX_METRICA_ID) || 0;
  const yandexMetricaInitedRef = useRef(false);
  const yandexInitialHitHandledRef = useRef(false);
  const [isLoggedIn, setIsLoggedIn] = useState(
    () => !!localStorage.getItem("token"),
  );
  const [currentFilter, setCurrentFilter] = useState("none");
  const [allGames, setAllGames] = useState(games);
  const [cart, setCart] = useState([]);
  const [cartAmount, setCartAmount] = useState(0);
  const [shownGames, setShownGames] = useState(allGames);
  const [reviewDisplay, setReviewDisplay] = useState(false);
  const [cartDisplayed, setCartDisplayed] = useState(false);
  const [profileDisplayed, setProfileDisplayed] = useState(false);
  const [search, setSearch] = useState("");
  const [overlap, setOverlap] = useState(false);
  const [searching, setSearching] = useState(false);
  const [browsing, setBrowsing] = useState(true);
  const [selectedGame, setSelectedGame] = useState(false);
  const [extended, setExtended] = useState(false);
  const [textExtended, setTextExtended] = useState(false);
  const [theme, setTheme] = useState(() => readStoredTheme() || "light");
  const isOverlayOpen = cartDisplayed || profileDisplayed;

  useEffect(() => {
    applyThemeToDocument(theme);
    try {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    } catch (e) {
      // ignore
    }
  }, [theme]);

  useEffect(() => {
    initI18n();
  }, []);

  // Token 无效等业务 401：清登录态并打开登录弹窗（由 request 拦截器派发事件）
  useEffect(() => {
    const onSessionInvalid = () => {
      setIsLoggedIn(false);
      setProfileDisplayed(false);
      setCartDisplayed(true);
    };
    window.addEventListener(SESSION_INVALID_EVENT, onSessionInvalid);
    return () =>
      window.removeEventListener(SESSION_INVALID_EVENT, onSessionInvalid);
  }, []);

  const [hoverState, setHoverState] = useState([
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
    {
      hovered: false,
      selected: false,
    },
  ]);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (!Number.isFinite(yandexMetricaId) || yandexMetricaId <= 0) return;
    if (yandexMetricaInitedRef.current) return;
    if (typeof window === "undefined" || typeof document === "undefined") return;

    (function (m, e, t, r, i, k, a) {
      m[i] =
        m[i] ||
        function () {
          (m[i].a = m[i].a || []).push(arguments);
        };
      m[i].l = Number(new Date());
      k = e.createElement(t);
      a = e.getElementsByTagName(t)[0];
      k.async = 1;
      k.src = r;
      a.parentNode.insertBefore(k, a);
    })(window, document, "script", "https://mc.yandex.ru/metrika/tag.js", "ym");

    window.ym(yandexMetricaId, "init", {
      clickmap: true,
      trackLinks: true,
      accurateTrackBounce: true,
      webvisor: true,
    });

    yandexMetricaInitedRef.current = true;
  }, [yandexMetricaId]);

  useEffect(() => {
    if (!Number.isFinite(yandexMetricaId) || yandexMetricaId <= 0) return;
    if (typeof window === "undefined" || typeof window.ym !== "function") return;

    // init 时 Metrica 会记录首次访问，这里只记录后续 SPA 路由切换
    if (!yandexInitialHitHandledRef.current) {
      yandexInitialHitHandledRef.current = true;
      return;
    }

    window.ym(
      yandexMetricaId,
      "hit",
      `${location.pathname}${location.search}${location.hash}`,
    );
  }, [
    yandexMetricaId,
    location.pathname,
    location.search,
    location.hash,
  ]);

  if (
    !isGameHomePath(location.pathname) &&
    selectedGame === false
  ) {
    let surname = location.pathname.substring(16);
    console.log("test");
    let currentGame = games.find(game => game.surname === surname);
    if (currentGame != undefined) {
      setSelectedGame(currentGame);
    } else {
      setSelectedGame(templateGame);
    }
  }

  async function handleBrowse() {
    setExtended(false);
    setTextExtended(false);
    setCartDisplayed(false);
    setHoverState([...hoverState, (hoverState[21].hovered = false)]);
    navigate(browsePath());
  }

  const handleHome = () => {
    setExtended(false);
    setTextExtended(false);
    setCartDisplayed(false);
    setHoverState([...hoverState, (hoverState[21].hovered = false)]);
    navigate(isLangCategoryRouting() ? gameHomePath() : browsePath());
  };

  const handleSearch = e => {
    setSearch(e.target.value);
    setSearching(false);
  };

  const handleSearchSubmit = e => {
    setCurrentFilter("none");
    e.preventDefault();
    setSearching(true);

    if (!isGameHomePath(location.pathname) && location.pathname !== browsePath()) {
      navigate(isLangCategoryRouting() ? gameHomePath() : browsePath());
    }
  };

  const handleSelect = e => {
    setCurrentFilter(filterNames[e.target.id - 8]);
  };

  const handleSelectGame = e => {
    if (e.target.tagName === "BUTTON") {
      return;
    } else if (e.target.classList[0] != "AddToCart_addToCart__zbJPe") {
      setSelectedGame(games[e.target.parentNode.id]);
      navigate(legacyGamePagePath(games[e.target.parentNode.id].surname));
    }
  };

  const handleLike = e => {
    let handledLike = allGames.map((game, i) => {
      if (e.target.id == i) {
        game.isLiked = !game.isLiked;
        return game;
      } else {
        return game;
      }
    });

    setAllGames(handledLike);
  };

  const clearFilter = () => {
    setCurrentFilter("none");
    setSearch("");
    setReviewDisplay(false);
  };

  const openGamePage = e => {
    setCartDisplayed(false);
    let selectedGameSurname = e.target.id;
    navigate(`/react-ecommerce-store/games/${selectedGameSurname}`);
  };

  const handleHover = e => {
    if (hoverState[e.target.id].selected) {
      return;
    }

    let newHoverState = hoverState.map((element, i) => {
      if (e.target.id == i) {
        element.hovered = !element.hovered;
        return element;
      } else {
        return element;
      }
    });

    setHoverState(newHoverState);
  };

  const handleHoverGame = e => {
    let handledHoveredGame = allGames.map((game, i) => {
      if (e.target.id == i) {
        game.isHovered = !game.isHovered;
        return game;
      } else {
        return game;
      }
    });

    setAllGames(handledHoveredGame);
  };

  const handleAddToCart = e => {
    let handledAddedGame = allGames.map((game, i) => {
      if (location.pathname === "/gamee/browse") {
        if (e.target.id == i) {
          game.inCart = true;
          let newCart = cart;
          newCart.push(game);
          setCart(newCart);
          setCartAmount(cartAmount + 1);
          return game;
        } else {
          return game;
        }
      } else {
        if (selectedGame.id == i) {
          game.inCart = true;
          let newCart = cart;
          newCart.push(game);
          setCart(newCart);
          setCartAmount(cartAmount + 1);
          return game;
        } else {
          return game;
        }
      }
    });

    setAllGames(handledAddedGame);
  };

  const clearCart = () => {
    setCart([]);
    setCartAmount(0);
    const defaultGames = allGames.map((game, i) => {
      game.inCart = false;
      game.isHovered = false;
      return game;
    });
    setAllGames(defaultGames);
    let newHoverState = hoverState[21];
    newHoverState.hovered = false;
    setHoverState([...hoverState, (hoverState[21] = newHoverState)]);
  };

  const handleRemoveFromCart = e => {
    let removedIndex = cart.findIndex(game => game.id == e.target.id);
    let newAllGames = allGames.map((game, i) => {
      if (game.id == e.target.id) {
        game.inCart = false;
        game.isHovered = false;
        return game;
      } else {
        return game;
      }
    });
    setAllGames(newAllGames);
    let firstHalf = cart.slice(0, removedIndex);
    let secondHalf = cart.slice(removedIndex + 1);
    let addedUp = firstHalf.concat(secondHalf);
    setCart(addedUp);
    setCartAmount(cartAmount - 1);
    setHoverState([...hoverState, (hoverState[21].hovered = false)]);
  };

  useEffect(() => {
    setOverlap(false);

    if (isGameHomePath(location.pathname)) {
      setBrowsing(false);
    } else {
      setBrowsing(true);
    }

    const isBrowsePage = location.pathname === browsePath() || location.pathname === `${browsePath()}/`;
    if (!isBrowsePage) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "scroll";
    }
  }, [location.pathname]);

  const handleOpenLogin = () => {
    setCartDisplayed(true);
  };

  const handleCloseCart = () => {
    setCartDisplayed(false);
  };

  const handleLoginSuccess = () => {
    setIsLoggedIn(true);
    fetchAndCacheCurrentUser().catch(() => {});
  };

  const handleOpenProfile = () => {
    const hasToken = !!localStorage.getItem("token");
    if (!isLoggedIn || !hasToken) {
      setProfileDisplayed(false);
      handleOpenLogin();
      return;
    }
    setProfileDisplayed(true);
  };

  const handleCloseProfile = () => {
    setProfileDisplayed(false);
  };

  // 已有 token 时补全当前用户 ID 缓存（刷新页面或 VK 登录跳转后）
  useEffect(() => {
    if (!isLoggedIn || getCachedCurrentUserId() != null) return;
    fetchAndCacheCurrentUser().catch(() => {});
  }, [isLoggedIn]);

  // 登录成功后先调一次心跳，之后每 10 分钟静默请求一次，异常不提示、不影响用户
  useEffect(() => {
    if (!isLoggedIn) return;
    const tick = () => {
      get("/user/heartbeat").catch(() => {});
    };
    tick();
    const id = setInterval(tick, 10 * 60 * 1000);
    return () => clearInterval(id);
  }, [isLoggedIn]);

  const handleLogout = async () => {
    try {
      await post("/user/logout");
    } catch (error) {
      console.warn("退出接口调用失败:", error);
    } finally {
      localStorage.removeItem("token");
      clearCurrentUserCache();
      setIsLoggedIn(false);
    }
  };

  useEffect(() => {
    console.log(selectedGame);
  }, [selectedGame]);

  useEffect(() => {
    if (cartDisplayed || profileDisplayed) {
      document.body.style.overflow = "hidden !important";
    } else {
      document.body.style.overflow = "scroll !important";
    }
  }, [cartDisplayed, profileDisplayed]);

  const homeElement = (
    <Home
      handleHover={handleHover}
      hoverState={hoverState}
      shownGames={shownGames}
      cart={cart}
      cartAmount={cartAmount}
      cartDisplayed={cartDisplayed}
      isOverlayOpen={isOverlayOpen}
      handleOpenLogin={handleOpenLogin}
      handleCloseCart={handleCloseCart}
      handleLoginSuccess={handleLoginSuccess}
      handleLogout={handleLogout}
      handleOpenProfile={handleOpenProfile}
      isLoggedIn={isLoggedIn}
      clearCart={clearCart}
      handleAddToCart={handleAddToCart}
      handleLike={handleLike}
      handleHoverGame={handleHoverGame}
      handleSelectGame={handleSelectGame}
      handleRemoveFromCart={handleRemoveFromCart}
      setHoverState={setHoverState}
      overlap={overlap}
      setOverlap={setOverlap}
      openGamePage={openGamePage}
      theme={theme}
      setTheme={setTheme}
    />
  );

  const notFoundElement = (
    <NotFound
      cartDisplayed={cartDisplayed}
      handleCloseCart={handleCloseCart}
      handleOpenLogin={handleOpenLogin}
      handleLoginSuccess={handleLoginSuccess}
      handleLogout={handleLogout}
      isLoggedIn={isLoggedIn}
      cartAmount={cartAmount}
      clearCart={clearCart}
      hoverState={hoverState}
      handleHome={handleHome}
      handleHover={handleHover}
      cart={cart}
      browsing={browsing}
      search={search}
      searching={searching}
      handleSearch={handleSearch}
      handleSearchSubmit={handleSearchSubmit}
      handleBrowse={handleBrowse}
      handleRemoveFromCart={handleRemoveFromCart}
      openGamePage={openGamePage}
    />
  );

  return (
    <ResponsiveProvider>
      <ToastProvider>
        {profileDisplayed && (
          <Profile isLoggedIn={isLoggedIn} onClose={handleCloseProfile} />
        )}
        <AnimatePresence exitBeforeEnter>
          <Routes key={location.pathname} location={location}>
            {isLangCategoryRouting() ? (
              <>
                <Route
                  path="/"
                  element={<Navigate to={gameHomePath()} replace />}
                />
                <Route
                  path="/gamee/*"
                  element={<Navigate to={gameHomePath()} replace />}
                />
                <Route path="/vk-callback" element={<VKCallback />} />
                <Route path="/gamee/vk-callback" element={<VKCallback />} />
                <Route
                  path="/:lang/game/:gameId"
                  element={
                    <LangDetailLayout>
                      <GameDetail
                        theme={theme}
                        setTheme={setTheme}
                        isLoggedIn={isLoggedIn}
                        handleOpenLogin={handleOpenLogin}
                        handleLogout={handleLogout}
                        handleOpenProfile={handleOpenProfile}
                        cartDisplayed={cartDisplayed}
                        handleCloseCart={handleCloseCart}
                        handleLoginSuccess={handleLoginSuccess}
                        hoverState={hoverState}
                        handleHover={handleHover}
                      />
                    </LangDetailLayout>
                  }
                />
                <Route
                  path="/:lang/collection/:collectionId"
                  element={
                    <LangDetailLayout>
                      <CollectionDetail
                        theme={theme}
                        setTheme={setTheme}
                        isLoggedIn={isLoggedIn}
                        handleOpenLogin={handleOpenLogin}
                        handleLogout={handleLogout}
                        cartDisplayed={cartDisplayed}
                        handleCloseCart={handleCloseCart}
                        handleLoginSuccess={handleLoginSuccess}
                      />
                    </LangDetailLayout>
                  }
                />
                <Route
                  path="/:lang/topic/:topicId"
                  element={
                    <LangDetailLayout>
                      <TopicDetail
                        theme={theme}
                        setTheme={setTheme}
                        isLoggedIn={isLoggedIn}
                        handleOpenLogin={handleOpenLogin}
                        cartDisplayed={cartDisplayed}
                        handleCloseCart={handleCloseCart}
                        handleLoginSuccess={handleLoginSuccess}
                      />
                    </LangDetailLayout>
                  }
                />
                <Route
                  path="/:lang/post/:postId"
                  element={
                    <LangDetailLayout>
                      <PostDetail
                        theme={theme}
                        setTheme={setTheme}
                        isLoggedIn={isLoggedIn}
                        handleOpenLogin={handleOpenLogin}
                        cartDisplayed={cartDisplayed}
                        handleCloseCart={handleCloseCart}
                        handleLoginSuccess={handleLoginSuccess}
                      />
                    </LangDetailLayout>
                  }
                />
                <Route
                  path="/:lang/:category"
                  element={<LangCategoryLayout>{homeElement}</LangCategoryLayout>}
                />
                <Route path="/:lang" element={<LangOnlyRedirect />} />
                <Route path="*" element={notFoundElement} />
              </>
            ) : (
              <>
                <Route path="/" element={<Navigate to="/gamee/" replace />} />
                <Route path="/vk-callback" element={<VKCallback />} />
                <Route path="/gamee/vk-callback" element={<VKCallback />} />
                <Route path="/gamee/" element={homeElement}>
                  <Route index element={null} />
                  <Route path="collections" element={null} />
                  <Route path="topics" element={null} />
                </Route>
                <Route
                  path="/gamee"
                  element={
                    <Browse
                      cart={cart}
                      cartAmount={cartAmount}
                      handleHover={handleHover}
                      handleSelect={handleSelect}
                      hoverState={hoverState}
                      currentFilter={currentFilter}
                      shownGames={shownGames}
                      setShownGames={setShownGames}
                      clearFilter={clearFilter}
                      reviewDisplay={reviewDisplay}
                      setReviewDisplay={setReviewDisplay}
                      allGames={allGames}
                      setAllGames={setAllGames}
                      handleLike={handleLike}
                      handleHoverGame={handleHoverGame}
                      handleAddToCart={handleAddToCart}
                      handleSelectGame={handleSelectGame}
                      handleSearch={handleSearch}
                      handleSearchSubmit={handleSearchSubmit}
                      search={search}
                      searching={searching}
                      browsing={browsing}
                      handleBrowse={handleBrowse}
                      handleHome={handleHome}
                      cartDisplayed={cartDisplayed}
                      handleOpenLogin={handleOpenLogin}
                      handleCloseCart={handleCloseCart}
                      handleLoginSuccess={handleLoginSuccess}
                      handleLogout={handleLogout}
                      isLoggedIn={isLoggedIn}
                      clearCart={clearCart}
                      handleRemoveFromCart={handleRemoveFromCart}
                      setHoverState={setHoverState}
                      openGamePage={openGamePage}
                    />
                  }
                />
                <Route
                  path="/gamee/games/:gameId"
                  element={
                    <GamePage
                      cart={cart}
                      cartAmount={cartAmount}
                      handleHover={handleHover}
                      hoverState={hoverState}
                      handleLike={handleLike}
                      handleAddToCart={handleAddToCart}
                      handleSelectGame={handleSelectGame}
                      selectedGame={selectedGame}
                      setSelectedGame={setSelectedGame}
                      handleSearch={handleSearch}
                      handleSearchSubmit={handleSearchSubmit}
                      search={search}
                      searching={searching}
                      browsing={browsing}
                      handleBrowse={handleBrowse}
                      handleHome={handleHome}
                      setHoverState={setHoverState}
                      allGames={allGames}
                      extended={extended}
                      setExtended={setExtended}
                      textExtended={textExtended}
                      setTextExtended={setTextExtended}
                      cartDisplayed={cartDisplayed}
                      handleOpenLogin={handleOpenLogin}
                      handleCloseCart={handleCloseCart}
                      handleLoginSuccess={handleLoginSuccess}
                      handleLogout={handleLogout}
                      isLoggedIn={isLoggedIn}
                      clearCart={clearCart}
                      handleRemoveFromCart={handleRemoveFromCart}
                      openGamePage={openGamePage}
                      theme={theme}
                      setTheme={setTheme}
                    />
                  }
                />
                <Route
                  path="/gamee/game/:gameId"
                  element={
                    <GameDetail
                      theme={theme}
                      setTheme={setTheme}
                      isLoggedIn={isLoggedIn}
                      handleOpenLogin={handleOpenLogin}
                      handleLogout={handleLogout}
                      handleOpenProfile={handleOpenProfile}
                      cartDisplayed={cartDisplayed}
                      handleCloseCart={handleCloseCart}
                      handleLoginSuccess={handleLoginSuccess}
                      hoverState={hoverState}
                      handleHover={handleHover}
                    />
                  }
                />
                <Route
                  path="/gamee/collection/:collectionId"
                  element={
                    <CollectionDetail
                      theme={theme}
                      setTheme={setTheme}
                      isLoggedIn={isLoggedIn}
                      handleOpenLogin={handleOpenLogin}
                      handleLogout={handleLogout}
                      cartDisplayed={cartDisplayed}
                      handleCloseCart={handleCloseCart}
                      handleLoginSuccess={handleLoginSuccess}
                    />
                  }
                />
                <Route
                  path="/gamee/topic/:topicId"
                  element={
                    <TopicDetail
                      theme={theme}
                      setTheme={setTheme}
                      isLoggedIn={isLoggedIn}
                      handleOpenLogin={handleOpenLogin}
                      cartDisplayed={cartDisplayed}
                      handleCloseCart={handleCloseCart}
                      handleLoginSuccess={handleLoginSuccess}
                    />
                  }
                />
                <Route
                  path="/gamee/post/:postId"
                  element={
                    <PostDetail
                      theme={theme}
                      setTheme={setTheme}
                      isLoggedIn={isLoggedIn}
                      handleOpenLogin={handleOpenLogin}
                      cartDisplayed={cartDisplayed}
                      handleCloseCart={handleCloseCart}
                      handleLoginSuccess={handleLoginSuccess}
                    />
                  }
                />
                <Route path="*" element={notFoundElement} />
              </>
            )}
          </Routes>
        </AnimatePresence>
        <BackTop />
      </ToastProvider>
    </ResponsiveProvider>
  );
}

export default App;
